<template>
  <div class="image-upload">
    <label v-if="label" class="iu-label">{{ label }}</label>
    <div
      class="iu-area"
      :class="{ dragging: dragging, uploading: uploading }"
      @click="triggerInput"
      @dragover.prevent="dragging = true"
      @dragleave.prevent="dragging = false"
      @drop.prevent="onDrop"
    >
      <div v-if="previewSrc" class="iu-preview">
        <img :src="previewSrc" alt="" />
        <button class="iu-remove" type="button" @click.stop="remove">×</button>
      </div>
      <div v-else class="iu-placeholder">
        <div class="iu-icon">📷</div>
        <p>点击或拖拽上传图片</p>
        <p class="iu-hint">支持 JPG/PNG，≤5MB</p>
      </div>
      <input
        ref="input"
        type="file"
        accept="image/*"
        class="iu-input"
        @change="onSelect"
      />
    </div>
    <span v-if="error" class="iu-error">{{ error }}</span>
  </div>
</template>

<script>
import { uploadMedia } from '@/api/media'

export default {
  name: 'ImageUpload',
  props: {
    // 原图 URL（v-model）
    value: { type: String, default: '' },
    // 缩略图 URL
    thumbValue: { type: String, default: '' },
    // 业务分类，决定后端落盘目录（如 product / banner=home/banners / ad-home / ad-product-category / ad-product-detail / idcard）
    category: { type: String, required: true },
    // 懒上传：默认 true，选图只暂存、不上传，交由父组件在「保存」时调用 ensureUploaded() 才真正上传；
    // 需要「选图即上传」的旧用法（如媒体库）可显式传 :lazy="false"
    lazy: { type: Boolean, default: true },
    label: { type: String, default: '' }
  },
  data() {
    return {
      dragging: false,
      uploading: false,
      localPreview: '',
      // 待上传文件（懒上传模式下选图后暂存于此）
      pendingFile: null,
      // 已上传结果 { url, thumb_url }（非懒模式选图即生成；懒模式保存时才生成）
      uploadedData: null,
      error: ''
    }
  },
  computed: {
    previewSrc() {
      return this.localPreview || this.value
    }
  },
  methods: {
    triggerInput() {
      if (this.uploading) return
      this.$refs.input.click()
    },
    onSelect(e) {
      const file = e.target.files[0]
      if (file) this.handleFile(file)
      e.target.value = ''
    },
    onDrop(e) {
      this.dragging = false
      const file = e.dataTransfer.files[0]
      if (file && file.type.startsWith('image/')) {
        this.handleFile(file)
      }
    },
    handleFile(file) {
      if (!file.type.startsWith('image/')) {
        this.error = '请选择图片文件'
        return
      }
      if (file.size > 5 * 1024 * 1024) {
        this.error = '图片大小不能超过5MB'
        return
      }

      this.error = ''
      this.localPreview = URL.createObjectURL(file)
      this.pendingFile = file
      this.uploadedData = null

      // 显式关闭懒上传时才选图即上传（默认懒上传，由父组件保存时调用 ensureUploaded）
      if (!this.lazy) {
        this.doUpload(file).catch(() => {})
      }
    },
    doUpload(file) {
      const formData = new FormData()
      formData.append('file', file)
      formData.append('category', this.category)

      this.uploading = true
      return uploadMedia(formData)
        .then(res => {
          if (res && res.code === 0 && res.data) {
            this.uploadedData = {
              url: res.data.url || '',
              thumb_url: res.data.thumb_url || ''
            }
            this.$emit('input', this.uploadedData.url)
            this.$emit('thumb-input', this.uploadedData.thumb_url)
            this.localPreview = ''
            return this.uploadedData
          }
          this.error = (res && res.message) || '上传失败'
          this.localPreview = ''
          return Promise.reject(new Error(this.error))
        })
        .catch(err => {
          this.error = '上传失败：' + (err.message || err)
          this.localPreview = ''
          return Promise.reject(err)
        })
        .finally(() => {
          this.uploading = false
        })
    },
    // 供父组件在「保存」时调用：懒上传模式才真正上传；已上传过（或选图即上传模式）直接返回结果
    ensureUploaded() {
      if (this.lazy && this.pendingFile && !this.uploadedData) {
        return this.doUpload(this.pendingFile)
      }
      return Promise.resolve(this.uploadedData || null)
    },
    remove() {
      this.localPreview = ''
      this.pendingFile = null
      this.uploadedData = null
      this.$emit('input', '')
      this.$emit('thumb-input', '')
    }
  }
}
</script>

<style scoped>
.image-upload {
  margin-bottom: 15px;
}

.iu-label {
  display: block;
  margin-bottom: 8px;
  font-size: 14px;
  color: #333;
}

.iu-area {
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 160px;
  height: 160px;
  border: 2px dashed #d9d9d9;
  border-radius: 8px;
  cursor: pointer;
  overflow: hidden;
  transition: all 0.3s;
}

.iu-area:hover,
.iu-area.dragging {
  border-color: #1890ff;
  background: rgba(24, 144, 255, 0.05);
}

.iu-area.uploading {
  opacity: 0.6;
  cursor: not-allowed;
}

.iu-placeholder {
  color: #999;
  text-align: center;
  font-size: 13px;
}

.iu-icon {
  font-size: 36px;
  margin-bottom: 8px;
}

.iu-hint {
  font-size: 12px;
  color: #ccc;
  margin-top: 4px;
}

.iu-preview {
  position: relative;
  width: 100%;
  height: 100%;
}

.iu-preview img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.iu-remove {
  position: absolute;
  top: -8px;
  right: -8px;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  background: #f5222d;
  color: #fff;
  border: none;
  cursor: pointer;
  font-size: 16px;
  line-height: 1;
}

.iu-input {
  display: none;
}

.iu-error {
  display: block;
  margin-top: 4px;
  font-size: 12px;
  color: #f5222d;
}
</style>
